import { useEffect, useState } from "react";
import { ClipboardCheck, Wrench, FileClock, Receipt, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

const day = (s) => (s ? new Date(s).toLocaleDateString("en-CA", { year: "numeric", month: "short", day: "numeric" }) : "—");
const cad = (n) => (n == null ? "—" : n.toLocaleString("en-CA", { style: "currency", currency: "CAD" }));
const state = (s) => (s || "").replace(/_/g, " ");

function Section({ icon: I, title, count, children, testid, open = false }) {
  return (
    <details open={open} className="rounded-2xl bg-black/5 px-3 py-2" data-testid={testid}>
      <summary className="cursor-pointer text-sm font-semibold flex items-center gap-2"><I className="w-4 h-4" />{title}<span className="ml-auto text-xs opacity-60">{count}</span></summary>
      <div className="mt-2 space-y-2 text-xs">{children}</div>
    </details>
  );
}

const Row = ({ a, b, c }) => <div className="flex gap-2 border-t border-black/10 pt-2"><span className="font-semibold shrink-0">{a}</span><span className="flex-1 min-w-0">{b}</span>{c && <span className="shrink-0 opacity-70">{c}</span>}</div>;

function LatestWR({ w }) {
  return (
    <Section icon={Wrench} title={`Latest work request · WR ${w.number}`} count={state(w.state)} testid="pj-latest-wr" open>
      <p className="text-sm">{w.description}</p>
      <Row a="Created" b={day(w.created)} c={w.updated && w.updated !== w.created ? `updated ${day(w.updated)}` : ""} />
      {w.hold_date && <Row a="On hold until" b={day(w.hold_date)} />}
      {(w.make || w.serial) && <Row a="Unit" b={[w.make, w.serial && `serial ${w.serial}`].filter(Boolean).join(" · ")} />}
      {w.downhole && <Row a="Downhole" b={w.downhole} />}
      {w.companies && <Row a="Service co." b={w.companies} />}
      {(w.cc || w.afe) && <Row a="CC / AFE" b={[w.cc, w.afe].filter(Boolean).join(" / ")} />}
      {w.estimates.map((e) => <Row key={e.number} a={`Est ${e.number}`} b={day(e.created)} c={state(e.state)} />)}
      {w.field_tickets.map((f) => <Row key={f.number} a={`FT ${f.number}`} b={[f.technician, day(f.performed)].filter(Boolean).join(" · ")} c={state(f.state)} />)}
      {w.attachments > 0 && <Row a="Attachments" b={`${w.attachments} on file in Pumpjack`} />}
    </Section>
  );
}

export function PumpjackRecords({ h, url, locked = false }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const src = url || `/public/hotspots/${h.id}/pumpjack`;
  const hid = h?.house_type_id || h?.id;
  useEffect(() => {
    setD(null); setErr("");
    api.get(src, { headers: { "X-Tour-Key": sessionStorage.getItem(`tourkey_${hid}`) || "" } })
      .then((r) => setD(r.data))
      .catch((e) => setErr(e.response?.status === 502 ? "Pumpjack records are unavailable right now." : "hide"));
  }, [src, hid]);
  if (err === "hide") return locked ? <p className="mt-3 text-sm opacity-70" data-testid="pumpjack-locked">Pumpjack records are only shown to signed-in staff, or on password-protected tours.</p> : null;
  if (err) return <p className="mt-3 text-xs opacity-70" data-testid="pumpjack-error">{err}</p>;
  if (!d) return <p className="mt-3 text-xs opacity-70 inline-flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" />Loading Pumpjack records…</p>;
  const li = d.last_inspection, id = d.identity || {};
  return (
    <div className="mt-4 space-y-2" data-testid="pumpjack-records">
      <div className="text-xs opacity-70">{[id.producer_name, id.area_name, d.link.surface, d.link.downhole].filter(Boolean).join(" · ")}</div>
      <div className="grid grid-cols-3 gap-2 text-center">
        <div className="rounded-xl bg-black/5 p-2"><p className="text-lg font-semibold" data-testid="pj-open-wrs">{d.open_work_requests.length}</p><p className="text-[10px] uppercase tracking-wider opacity-60">Open WRs</p></div>
        <div className="rounded-xl bg-black/5 p-2"><p className="text-lg font-semibold" data-testid="pj-awaiting">{d.estimates_awaiting.length}</p><p className="text-[10px] uppercase tracking-wider opacity-60">Awaiting approval</p></div>
        <div className="rounded-xl bg-black/5 p-2"><p className="text-sm font-semibold mt-1" data-testid="pj-unpaid">{cad(d.financials.unpaid_total)}</p><p className="text-[10px] uppercase tracking-wider opacity-60">Unpaid</p></div>
      </div>
      {d.latest_work_request && <LatestWR w={d.latest_work_request} />}
      <Section icon={ClipboardCheck} title="Last inspection" count={li ? day(li.date) : "None"} testid="pj-inspection">
        {li ? <>
          <Row a="By" b={[li.technician, li.company].filter(Boolean).join(", ")} />
          {li.pump && <Row a="Pump" b={[li.pump, li.make].filter(Boolean).join(" · ")} />}
          <Row a="Signed off" b={li.signed_off_by ? `${li.signed_off_by}, ${day(li.signed_off_on)}` : "Not yet"} />
          {li.notes.map((n, i) => <Row key={i} a={n.item} b={n.note} />)}
        </> : <p className="opacity-70">No inspection on record for this well.</p>}
      </Section>
      <Section icon={Wrench} title="Open work requests" count={d.open_work_requests.length} testid="pj-wrs">
        {d.open_work_requests.map((w) => <Row key={w.number} a={`WR ${w.number}`} b={<>{w.description}<span className="block opacity-60">{[day(w.created), w.downhole, w.companies].filter(Boolean).join(" · ")}</span></>} c={state(w.state)} />)}
      </Section>
      <Section icon={FileClock} title="Estimates awaiting approval" count={d.estimates_awaiting.length} testid="pj-estimates">
        {d.estimates_awaiting.map((e, i) => <Row key={i} a={`WR ${e.number}`} b={<>{e.description || [e.company, e.by].filter(Boolean).join(" · ")}<span className="block opacity-60">{[day(e.created), e.company].filter(Boolean).join(" · ")}</span></>} c={e.total == null ? "" : cad(e.total)} />)}
      </Section>
      <Section icon={Receipt} title="Field tickets" count={d.live ? d.field_tickets.length : `${d.unpaid_field_tickets.length} unpaid of ${d.field_tickets.length}`} testid="pj-tickets">
        {d.field_tickets.map((f) => <Row key={f.number} a={`FT ${f.number}`} b={`WR ${f.wr} · ${[f.technician, day(f.performed || f.created), state(f.state)].filter(Boolean).join(" · ")}${f.invoice ? ` · Inv ${f.invoice}` : ""}`} c={f.total == null ? "" : cad(f.total)} />)}
      </Section>
      <p className="text-[10px] opacity-50">{d.live ? "Live from Pumpjack Connect" : "From Pumpjack records"} · revenue to date {cad(d.financials.revenue)}</p>
    </div>
  );
}
